import React from 'react';
import { Battery, Footprints, Gauge, HeartPulse, Mountain, Volume2, VolumeX, X, type LucideIcon } from 'lucide-react';
import { api, type HuntParticipantStatus } from '../api';
import { RADIUS, useTheme } from '../theme';
import { Icon } from '../components/ui';
import {
  findStatus, formatAge, formatAltitude, formatBattery, formatHeartRate, formatSpeaker, formatSpeed, formatSteps,
} from './huntStatus';

// KA1: kortet som visas när man trycker på en deltagare på kartan. Hämtar
// statusen själv var 10:e sekund medan det är öppet.

const POLL_MS = 10_000;

export function ParticipantStatusCard({ meetingId, participantKey, name, avatarUrl, onClose }: {
  meetingId: string; participantKey: string; name: string; avatarUrl?: string | null; onClose: () => void;
}) {
  const T = useTheme();
  const [rows, setRows] = React.useState<HuntParticipantStatus[] | null>(null);
  const [failed, setFailed] = React.useState(false);
  React.useEffect(() => {
    let alive = true;
    const load = () => api.huntParticipantStatus(meetingId)
      .then(r => { if (alive) { setRows(r); setFailed(false); } })
      .catch(() => { if (alive) { setFailed(true); setRows(prev => prev ?? []); } });
    void load();
    const tid = setInterval(load, POLL_MS);
    return () => { alive = false; clearInterval(tid); };
  }, [meetingId]);
  React.useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const s = rows ? findStatus(rows, participantKey) : null;
  const items: { icon: LucideIcon; label: string; value: string | null }[] = [
    { icon: Battery, label: 'Batteri', value: s ? formatBattery(s.battery, s.charging) : null },
    { icon: Footprints, label: 'Steg i dag', value: s ? formatSteps(s.steps) : null },
    { icon: HeartPulse, label: 'Puls', value: s ? formatHeartRate(s.heartRate) : null },
    { icon: Gauge, label: 'Hastighet', value: s ? formatSpeed(s.speed) : null },
    { icon: Mountain, label: 'Höjd', value: s ? formatAltitude(s.altitude) : null },
    { icon: s?.speakerOn === false ? VolumeX : Volume2, label: 'Högtalare', value: s ? formatSpeaker(s.speakerOn) : null },
  ];
  const missing = s ? items.filter(i => i.value == null).length : 0;
  const initials = name.split(/\s+/).filter(Boolean).slice(0, 2).map(w => w[0]).join('').toUpperCase();
  const device = s ? (s.sources.length > 1 ? 'telefon och klocka' : s.sources[0] === 'watch' ? 'klockan' : 'telefonen') : '';

  return (
    <div role="dialog" aria-label={`Status för ${name}`} data-participant-status style={{
      position: 'fixed', left: 12, right: 12, bottom: 'calc(96px + env(safe-area-inset-bottom, 0px))', zIndex: 9998,
      maxWidth: 420, margin: '0 auto', background: T.surface, color: T.text, border: `1px solid ${T.border}`,
      borderRadius: RADIUS.lg, boxShadow: T.shadow.raised, padding: 16,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        {avatarUrl
          ? <img src={avatarUrl} alt="" style={{ width: 44, height: 44, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }} />
          : <span aria-hidden style={{ width: 44, height: 44, borderRadius: '50%', background: T.amberDim, color: T.accentText, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, flexShrink: 0 }}>{initials || '?'}</span>}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 16, fontWeight: 800, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{name}</div>
          <div style={{ fontSize: 12.5, color: T.textMuted, marginTop: 2 }}>
            {!rows ? 'Hämtar …' : s ? `Från ${device} · ${formatAge(s.updatedAt)}` : failed ? 'Kunde inte hämta status' : 'Ingen status ännu'}
          </div>
        </div>
        <button type="button" onClick={onClose} aria-label="Stäng" style={{
          width: 44, height: 44, borderRadius: '50%', border: 'none', background: T.surface2, color: T.text,
          display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', flexShrink: 0,
        }}><Icon as={X} /></button>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginTop: 14 }}>
        {items.map(i => (
          <div key={i.label} style={{ display: 'flex', alignItems: 'center', gap: 10, background: T.surface2, borderRadius: RADIUS.md, padding: '10px 12px', minWidth: 0 }}>
            <Icon as={i.icon} size={18} color={i.value == null ? T.textMuted : T.accentText} />
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 11.5, color: T.textMuted }}>{i.label}</div>
              <div style={{ fontSize: 14, fontWeight: 700, color: i.value == null ? T.textMuted : T.text, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{i.value ?? '–'}</div>
            </div>
          </div>
        ))}
      </div>
      {rows && (!s || missing > 0) && (
        <div style={{ fontSize: 12, color: T.textMuted, marginTop: 10, lineHeight: 1.45 }}>
          {s ? 'Streck = telefonen eller klockan skickar inte det värdet.' : `${name.split(' ')[0]} har en äldre app eller har inte skickat något ännu.`}
        </div>
      )}
    </div>
  );
}
